import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import PaymentMethodModal from "../components/Modal/PaymentMethodModal";
import { useUpgradeLogic } from "../hooks/useUpgradeLogic";
import {
  getAnimationVariants,
  getCurrentPlanName,
} from "../utils/upgradeHelper";
import AccessGuard from "../components/memberships/upgradeMembershipPage/AccessGuard";
import UpgradeCard from "../components/memberships/upgradeMembershipPage/UpgradeCard";
import { BackButton } from "../components/memberships/upgradeMembershipPage/BackButton";
import { getEffectivePlan } from "../utils/subscriptionUtils";

const UpgradeMembershipPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, currentSubscription } = useSelector(
    (state) => state.auth,
  );
  const { newPlan } = location.state || {};
  // isTrial: true means user starts a 30-day trial with no payment required
  const isTrial = location.state?.isTrial ?? false;

  const currentPlan = getEffectivePlan(currentSubscription);

  const {
    isPaymentModalOpen,
    isProcessing,
    isSubscriptionLoading,
    handleUpgradeClick,
    handleExistingCardPayment,
    handleNewCardPayment,
    handleModalClose,
  } = useUpgradeLogic(newPlan, isTrial);

  const { pageVariants, itemVariants } = getAnimationVariants();
  const currentPlanName = getCurrentPlanName(currentPlan);
  const isLoading = isProcessing || isSubscriptionLoading;

  useEffect(() => {
    if (user === undefined) return;
    if (!user) {
      navigate("/");
      return;
    }
    if (user?.role === "buyer") {
      navigate("/profile");
    }
  }, []);

  return (
    <AccessGuard
      isAuthenticated={isAuthenticated}
      user={user}
      newPlan={newPlan}
    >
      <motion.div
        className="w-full max-w-2xl mx-auto py-8"
        variants={pageVariants}
        initial="hidden"
        animate="visible"
      >
        <BackButton variants={itemVariants} />

        <UpgradeCard
          variants={itemVariants}
          newPlan={newPlan}
          isTrial={isTrial}
          currentPlanName={currentPlanName}
          onUpgradeClick={handleUpgradeClick}
          isLoading={isLoading}
        />
      </motion.div>

      {/* Payment modal is only shown for non-trial upgrades */}
      {!isTrial && (
        <PaymentMethodModal
          isOpen={isPaymentModalOpen}
          onClose={handleModalClose}
          onExistingCardSelect={handleExistingCardPayment}
          onNewCardSelect={handleNewCardPayment}
          isLoading={isLoading}
        />
      )}
    </AccessGuard>
  );
};

export default UpgradeMembershipPage;
